/**
 * Folder create/edit modal
 */
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { IoFolderOpen } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import ColorInput, { DEFAULT_PRESET_COLORS } from '@/components/ColorInput/ColorInput';
import Modal from '@/components/Modal/Modal';
import type { Folder } from '@/types/card';
import { useCreateFolderMutation, useFoldersQuery, useUpdateFolderMutation } from '../hooks/api';

export interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder?: Folder | null;
  /** Pre-selected parent folder ID (for "Create sub-folder" action) */
  defaultParentId?: string | null;
}

export default function FolderModal({ isOpen, onClose, folder, defaultParentId }: FolderModalProps): ReactElement {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);

  const { data: folders } = useFoldersQuery();
  const createMutation = useCreateFolderMutation();
  const updateMutation = useUpdateFolderMutation();

  const isEditing = !!folder;
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Build list of selectable parent folders (exclude self and descendants when editing)
  const availableParents = useMemo(() => {
    if (!folders) return [];

    // When editing, we need to exclude the folder itself and all its descendants
    const getDescendantIds = (folderId: string): string[] => {
      const children = folders.filter((f) => f.parent_id === folderId);
      return [folderId, ...children.flatMap((c) => getDescendantIds(c.id))];
    };

    const excludeIds = isEditing ? getDescendantIds(folder.id) : [];
    return folders.filter((f) => !excludeIds.includes(f.id));
  }, [folders, folder, isEditing]);

  // Reset form when modal opens/folder changes
  useEffect(() => {
    if (isOpen) {
      setName(folder?.name ?? '');
      setColor(folder?.color ?? null);
      // Use defaultParentId for new folders, folder.parent_id for editing
      setParentId(folder?.parent_id ?? defaultParentId ?? null);
    }
  }, [isOpen, folder, defaultParentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: folder.id,
          data: { name: trimmedName, color, parent_id: parentId },
        });
      } else {
        await createMutation.mutateAsync({
          name: trimmedName,
          color,
          parent_id: parentId,
        });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save folder:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Folder' : 'Create Folder'} size="sm">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name input */}
        <div>
          <label htmlFor="folder-name" className="text-silver mb-1.5 block text-sm font-medium">
            Name
          </label>
          <input
            id="folder-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter folder name..."
            className="border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-full rounded-lg border px-4 py-2.5 text-sm"
            autoFocus
            maxLength={100}
            required
          />
        </div>

        {/* Parent folder selector */}
        {availableParents.length > 0 && (
          <div>
            <label htmlFor="folder-parent" className="text-silver mb-1.5 block text-sm font-medium">
              Parent Folder
            </label>
            <div className="relative">
              <IoFolderOpen className="text-ash pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <select
                id="folder-parent"
                value={parentId ?? ''}
                onChange={(e) => setParentId(e.target.value || null)}
                className="border-storm bg-slate text-pearl focus-ring w-full appearance-none rounded-lg border py-2.5 pr-4 pl-9 text-sm"
              >
                <option value="">None (root level)</option>
                {availableParents.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-ash mt-1 text-xs">Nest this folder inside another folder</p>
          </div>
        )}

        {/* Color picker */}
        <ColorInput
          value={color}
          onChange={setColor}
          presets={DEFAULT_PRESET_COLORS}
          label="Color"
          disabled={isPending}
          id="folder-color"
        />

        {/* Preview */}
        <div className="bg-slate rounded-lg p-3">
          <span className="text-ash mb-2 block text-xs tracking-wider uppercase">Preview</span>
          <div className="flex items-center gap-2">
            <IoFolderOpen className="h-4 w-4 shrink-0" style={{ color: color ?? undefined }} />
            <span className="text-pearl text-sm">{name.trim() || 'Folder name'}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="border-storm flex justify-end gap-3 border-t pt-4">
          <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
          <Button
            type="submit"
            text={isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Folder'}
            color="create"
            onClick={() => {}}
            disabled={!name.trim() || isPending}
          />
        </div>
      </form>
    </Modal>
  );
}
